import { Link } from "@tanstack/react-router";
import { useMemo, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";

import type { ReportFilters as Filters } from "../api/query-keys";
import { usePromotionReport } from "../api/use-promotion-report";
import { ReportFilters } from "../components/ReportFilters";
import { ReportTable } from "../components/ReportTable";
import { downloadCsv, reportToCsv } from "../lib/report-csv";

interface PromotionReportPageProps {
  initialFilters?: Filters;
}

export function PromotionReportPage({ initialFilters }: PromotionReportPageProps): JSX.Element {
  const { t } = useTranslation(["promotion", "nav"]);
  const [filters, setFilters] = useState<Filters>(initialFilters ?? {});
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const { data, isLoading, isError } = usePromotionReport(filters);

  const items = useMemo(() => data?.items ?? [], [data]);
  const pageItems = useMemo(() => items.slice(offset, offset + limit), [items, offset, limit]);
  // Shop filter options: distinct shop names from the loaded rows (no BE list endpoint).
  const shops = useMemo(() => [...new Set(items.map((r) => r.shop))].sort(), [items]);

  function updateFilters<K extends keyof Filters>(key: K, value: Filters[K] | undefined): void {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
    setOffset(0);
  }

  // Month picker sets both range keys at once (or clears them for "All time").
  function updateMonth(range: { date_from: string; date_to: string } | null): void {
    setFilters((prev) => {
      const next = { ...prev };
      if (range) {
        next.date_from = range.date_from;
        next.date_to = range.date_to;
      } else {
        delete next.date_from;
        delete next.date_to;
      }
      return next;
    });
    setOffset(0);
  }

  function handleExport(): void {
    downloadCsv(
      `promotion-report-${new Date().toISOString().slice(0, 10)}.csv`,
      reportToCsv(items),
    );
    toast.success(t("report.export_done", { count: items.length }));
  }

  return (
    <div data-testid="promotion-report-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.promotion")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("promotion:report.title")}</span>
          </>
        }
        title={t("promotion:report.title")}
        subtitle={t("promotion:report.subtitle")}
        actions={
          // Spec §5.4: one secondary entry; report rows are gift SKU × marketplace, not promotions.
          <Link
            to="/promotion"
            search={{ status: "finish" }}
            data-testid="report-performance-link"
            className="inline-flex h-9 items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] shadow-[var(--sh-xs)] transition hover:bg-[var(--canvas)]"
          >
            {t("promotion:performance.report_link")}
          </Link>
        }
      />

      <ReportFilters
        filters={filters}
        shops={shops}
        onChange={updateFilters}
        onMonthChange={updateMonth}
        onExport={handleExport}
        exportDisabled={items.length === 0}
      />

      <ReportTable items={pageItems} isLoading={isLoading} isError={isError} />

      {!isLoading && !isError && (
        <Pagination
          total={items.length}
          offset={offset}
          limit={limit}
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={({ offset: nextOffset, limit: nextLimit }) => {
            setOffset(nextOffset);
            setLimit(nextLimit);
          }}
        />
      )}
    </div>
  );
}
